/**
 * 読取パターンの item_list を Firestore 保存用（snake_case）/ アプリ用（camelCase）の
 * 間で相互変換するユーティリティ。
 *
 * 以前は create / update / read / history の各APIファイルに同一ロジックが重複していたため、
 * #2815（項目バリデーション追加）でフィールドが増えるのを機に共通化した。
 */
import { type ItemChoiceType, type ItemListType, type ItemListChildType } from '../../types/common';
import { isValidDateOutputFormat, DEFAULT_DATE_OUTPUT_FORMAT } from '../../constants/options';
import {
  isChoiceDataType,
  isCalculatableDataType,
  itemDataType,
  supportsCharTypeConstraint,
  supportsDateRangeConstraint,
  supportsPatternConstraint,
  supportsReviewChoice,
  type DateRangeUnit,
  type ItemCharType,
} from '../../constants/itemValidation';
import { fillMissingFieldCodes } from './fieldCode';
import { normalizeChoiceList, sanitizeChoices } from './choiceUtils';

/** データ型が未指定のときの既定値。Firestore 保存時に補完する */
const DEFAULT_DATA_TYPE: string = itemDataType.string;

/**
 * 日付の出力形式を Firestore 保存用に整える（#3144）。
 * 未指定は従来どおり空文字。選択肢外の不正な値のみデフォルトへ丸める。
 */
const toSafeOutputFormat = (value: string | undefined): string => {
  if (!value) return '';
  return isValidDateOutputFormat(value) ? value : DEFAULT_DATE_OUTPUT_FORMAT;
};

/**
 * 選択肢を Firestore 保存用に整える（#3075）。
 * 選択肢型（radio / select）以外の項目には選択肢を残さない。
 * 型を変更したあとに古い選択肢が残り、プロンプトや表示に紛れ込むのを防ぐ。
 */
const toSafeChoices = (
  type: string | undefined,
  choices: ItemChoiceType[] | undefined,
): ItemChoiceType[] => {
  if (!isChoiceDataType(type) || !choices) return [];
  return sanitizeChoices(choices);
};

/**
 * 計算フィールドの設定を Firestore 保存用に整える（#3145 / #3195）。
 * 計算できない型（日付・選択肢型・複数行データ）には計算設定を残さない。
 *
 * 型を変更しても計算設定が残ると、項目設定モーダルではトグルが非表示になって
 * OFF に見えるのに、applyCalculatedFields は is_calculated だけを見て計算を適用するため、
 * 画面表示と実際の値が食い違う。選択肢（toSafeChoices）と同じ考え方で、
 * 全保存経路が通るここでクリアする。
 */
const toSafeCalculation = (
  type: string | undefined,
  isCalculated: boolean | undefined,
  formula: string | undefined,
): { is_calculated: boolean; formula: string } => {
  // 型未設定は保存時と同じく string（テキスト）とみなす。
  // 生の type で判定すると、型を明示していない既存項目の計算設定まで消えてしまう
  if (!isCalculatableDataType(type ?? DEFAULT_DATA_TYPE)) {
    return { is_calculated: false, formula: '' };
  }
  return { is_calculated: isCalculated ?? false, formula: formula ?? '' };
};

/** 外部由来の値を文字列配列にする（#3357）。配列でなければ undefined */
const toStringArray = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value)) return undefined;
  const values = value.filter((v): v is string => typeof v === 'string');
  return values.length > 0 ? values : undefined;
};

/**
 * 要注意にする選択肢（#3357）を保存用に整える。
 *
 * 選択肢型以外には残さない。また、現在の選択肢に存在しない値も落とす。
 * 選択肢を改名・削除したときに取り残された値が残ると、誰も選べない値を
 * 待ち続けて要注意が出なくなり、壊れていることに気づけないため。
 */
const toSafeReviewChoiceValues = (
  type: string | undefined,
  choices: ItemChoiceType[],
  values: string[] | undefined,
): string[] => {
  if (!supportsReviewChoice(type) || !values) return [];
  // 選択肢の出力値（value が空なら label を使う。#3075 の既定）
  const available = new Set(choices.map(choice => choice.value || choice.label));
  return values.filter(value => available.has(value));
};

/**
 * 型に合わないバリデーション制約を落とす（#3357）。
 *
 * 文字種・入力パターンはテキスト系、日付範囲は日付型でしか設定できない。
 * 表示（項目設定モーダル）を絞るだけでは、非表示になった設定が Firestore に残り続け、
 * モーダル上は未設定に見えるのに検証だけが効く、という食い違いが起きる。
 * 計算設定（toSafeCalculation）・選択肢（toSafeChoices）と同じく、
 * 全保存経路が通るここで落とす。JSON 取り込みなどモーダルを経由しない経路もカバーできる。
 */
const toSafeValidation = <T extends ValidationSnake>(
  type: string | undefined,
  validation: T,
): T => {
  // 型未設定は保存時と同じく string（テキスト）とみなす（既存項目の設定を消さないため）
  const dataType = type ?? DEFAULT_DATA_TYPE;
  const safe = { ...validation };

  if (!supportsCharTypeConstraint(dataType)) {
    delete safe.char_type;
  }
  if (!supportsPatternConstraint(dataType)) {
    delete safe.pattern;
    delete safe.pattern_message;
  }
  if (!supportsDateRangeConstraint(dataType)) {
    delete safe.date_no_past;
    delete safe.date_no_future;
    delete safe.date_past_limit;
    delete safe.date_past_unit;
    delete safe.date_future_limit;
    delete safe.date_future_unit;
  }
  return safe;
};

// バリデーション制約フィールド（#2815 / #3357）。未設定（undefined）の場合は Firestore に保存しない。
type ValidationSnake = {
  required?: boolean;
  min_length?: number;
  max_length?: number;
  min_value?: number;
  max_value?: number;
  treat_zero_as_empty?: boolean;
  // 文字種・入力パターン・日付範囲（#3357）
  char_type?: ItemCharType;
  pattern?: string;
  pattern_message?: string;
  date_no_past?: boolean;
  date_no_future?: boolean;
  date_past_limit?: number;
  date_past_unit?: DateRangeUnit;
  date_future_limit?: number;
  date_future_unit?: DateRangeUnit;
  // 要注意にする選択肢の出力値（#3357）
  review_choice_values?: string[];
  // 読み取り精度が低いときに要注意にするか（#3384）。false のときだけ保存する
  review_low_confidence?: boolean;
};

// Firestore 保存用の子項目（snake_case）
export type ItemListChildSnake = {
  id: string;
  child_value: string;
  field_code: string; // フィールドコード（#3175）
  type: string;
  display_width: number;
  is_active: boolean;
  is_ocr_target: boolean;
  extraction_rule: string;
  output_format: string;
  fixed_value: string;
  is_calculated: boolean; // 計算フィールドフラグ（#3145）
  formula: string; // 計算式（#3145）。項目参照はフィールドコードで保持
  // 選択肢（#3075）。明細の列ではプルダウン（select）のみ使える
  choices: ItemChoiceType[];
} & ValidationSnake;

// Firestore 保存用の親項目（snake_case）
export type ItemListSnake = {
  id: string;
  parent_value: string;
  field_code: string; // フィールドコード（#3175）
  type: string;
  display_width: number;
  is_active: boolean;
  is_ocr_target: boolean;
  extraction_rule: string;
  output_format: string;
  fixed_value: string;
  is_calculated: boolean; // 計算フィールドフラグ（#3145）
  formula: string; // 計算式（#3145）。項目参照はフィールドコードで保持
  // 選択肢（#3075）。label / value とも1語のため snake_case でも同じ形になる
  choices: ItemChoiceType[];
  children: ItemListChildSnake[];
} & ValidationSnake;

// Firestore 取得直後の子項目（各フィールドは欠損しうる）
type ItemListChildRaw = {
  id: string;
  child_value: string;
  field_code?: string;
  type?: string;
  display_width?: number;
  is_active?: boolean;
  is_ocr_target?: boolean;
  extraction_rule?: string;
  output_format?: string;
  fixed_value?: string;
  is_calculated?: boolean;
  formula?: string;
  choices?: unknown; // 選択肢（#3075）。外部由来のため normalizeChoiceList で検証する
  required?: boolean;
  min_length?: number;
  max_length?: number;
  min_value?: number;
  max_value?: number;
  // 子項目にも 0 を空欄扱い（#3262）の設定はあるが、型に書かれていなかった。
  // toValidationCamel は実行時に読むため動作はしていたが、宣言を親と揃える
  treat_zero_as_empty?: boolean;
  // 文字種・入力パターン・日付範囲（#3357）
  char_type?: ItemCharType;
  pattern?: string;
  pattern_message?: string;
  date_no_past?: boolean;
  date_no_future?: boolean;
  date_past_limit?: number;
  date_past_unit?: DateRangeUnit;
  date_future_limit?: number;
  date_future_unit?: DateRangeUnit;
  // 要注意にする選択肢（#3357）。外部由来のため unknown で受ける
  review_choice_values?: unknown;
  review_low_confidence?: boolean; // 読み取り精度が低いときに要注意にするか（#3384）
};

// Firestore 取得直後の親項目（各フィールドは欠損しうる）
type ItemListRaw = {
  id: string;
  parent_value: string;
  field_code?: string;
  type?: string;
  display_width?: number;
  is_active?: boolean;
  is_ocr_target?: boolean;
  extraction_rule?: string;
  output_format?: string;
  fixed_value?: string;
  is_calculated?: boolean;
  formula?: string;
  choices?: unknown; // 選択肢（#3075）。外部由来のため normalizeChoiceList で検証する
  required?: boolean;
  min_length?: number;
  max_length?: number;
  min_value?: number;
  max_value?: number;
  treat_zero_as_empty?: boolean;
  // 文字種・入力パターン・日付範囲（#3357）
  char_type?: ItemCharType;
  pattern?: string;
  pattern_message?: string;
  date_no_past?: boolean;
  date_no_future?: boolean;
  date_past_limit?: number;
  date_past_unit?: DateRangeUnit;
  date_future_limit?: number;
  date_future_unit?: DateRangeUnit;
  // 要注意にする選択肢（#3357）。外部由来のため unknown で受ける
  review_choice_values?: unknown;
  review_low_confidence?: boolean; // 読み取り精度が低いときに要注意にするか（#3384）
  children?: ItemListChildRaw[];
};

// camelCase 側の制約フィールド（ItemValidationType 相当）
type ItemValidationSource = {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
  treatZeroAsEmpty?: boolean;
  // 文字種・入力パターン・日付範囲（#3357）
  charType?: ItemCharType;
  pattern?: string;
  patternMessage?: string;
  dateNoPast?: boolean;
  dateNoFuture?: boolean;
  datePastLimit?: number;
  datePastUnit?: DateRangeUnit;
  dateFutureLimit?: number;
  dateFutureUnit?: DateRangeUnit;
  // 要注意にする選択肢の出力値（#3357）
  reviewChoiceValues?: string[];
  reviewLowConfidence?: boolean; // 読み取り精度が低いときに要注意にするか（#3384）
};

// camelCase の制約値を snake_case に変換する（値が存在するフィールドのみ含める）
const toValidationSnake = (item: ItemValidationSource): ValidationSnake => {
  const validation: ValidationSnake = {};
  if (item.required !== undefined) validation.required = item.required;
  if (item.minLength !== undefined) validation.min_length = item.minLength;
  if (item.maxLength !== undefined) validation.max_length = item.maxLength;
  if (item.minValue !== undefined) validation.min_value = item.minValue;
  if (item.maxValue !== undefined) validation.max_value = item.maxValue;
  if (item.treatZeroAsEmpty !== undefined) validation.treat_zero_as_empty = item.treatZeroAsEmpty;
  // 文字種・入力パターン・日付範囲（#3357）
  if (item.charType !== undefined) validation.char_type = item.charType;
  if (item.pattern !== undefined) validation.pattern = item.pattern;
  if (item.patternMessage !== undefined) validation.pattern_message = item.patternMessage;
  if (item.dateNoPast !== undefined) validation.date_no_past = item.dateNoPast;
  if (item.dateNoFuture !== undefined) validation.date_no_future = item.dateNoFuture;
  if (item.datePastLimit !== undefined) validation.date_past_limit = item.datePastLimit;
  if (item.datePastUnit !== undefined) validation.date_past_unit = item.datePastUnit;
  if (item.dateFutureLimit !== undefined) validation.date_future_limit = item.dateFutureLimit;
  if (item.dateFutureUnit !== undefined) validation.date_future_unit = item.dateFutureUnit;
  // 要注意にする選択肢（#3357）。空配列は保存しない（未設定と同じ扱い）
  if (item.reviewChoiceValues !== undefined && item.reviewChoiceValues.length > 0) {
    validation.review_choice_values = item.reviewChoiceValues;
  }
  // 読み取り精度の要注意（#3384）。デフォルト ON のため、OFF のときだけ保存する
  if (item.reviewLowConfidence === false) validation.review_low_confidence = false;
  return validation;
};

// snake_case の制約値を camelCase に変換する（値が存在するフィールドのみ含める）
const toValidationCamel = (item: ValidationSnake): ItemValidationSource => {
  const validation: ItemValidationSource = {};
  if (item.required !== undefined) validation.required = item.required;
  if (item.min_length !== undefined) validation.minLength = item.min_length;
  if (item.max_length !== undefined) validation.maxLength = item.max_length;
  if (item.min_value !== undefined) validation.minValue = item.min_value;
  if (item.max_value !== undefined) validation.maxValue = item.max_value;
  if (item.treat_zero_as_empty !== undefined)
    validation.treatZeroAsEmpty = item.treat_zero_as_empty;
  // 文字種・入力パターン・日付範囲（#3357）
  if (item.char_type !== undefined) validation.charType = item.char_type;
  if (item.pattern !== undefined) validation.pattern = item.pattern;
  if (item.pattern_message !== undefined) validation.patternMessage = item.pattern_message;
  if (item.date_no_past !== undefined) validation.dateNoPast = item.date_no_past;
  if (item.date_no_future !== undefined) validation.dateNoFuture = item.date_no_future;
  if (item.date_past_limit !== undefined) validation.datePastLimit = item.date_past_limit;
  if (item.date_past_unit !== undefined) validation.datePastUnit = item.date_past_unit;
  if (item.date_future_limit !== undefined) validation.dateFutureLimit = item.date_future_limit;
  if (item.date_future_unit !== undefined) validation.dateFutureUnit = item.date_future_unit;
  // 要注意にする選択肢（#3357）。呼び出し側で toStringArray を通している
  if (item.review_choice_values !== undefined && item.review_choice_values.length > 0) {
    validation.reviewChoiceValues = item.review_choice_values;
  }
  if (item.review_low_confidence === false) validation.reviewLowConfidence = false; // #3384
  return validation;
};

/**
 * itemList を Firestore 用の snake_case に変換する。
 * type / display_width / is_active / extraction_rule / output_format は
 * 必須フィールドとしてデフォルト値を設定する。
 */
export const convertItemListToSnakeCase = (itemList: ItemListType[]): ItemListSnake[] => {
  // フィールドコード未設定の項目をここで補完する（#3175）。
  // 全保存経路がこの関数を通るため、ここで埋めておけば Firestore にコード無しの項目が残らない。
  // 設定済みのコードは書き換えない（テンプレート・計算式の参照が壊れるため）。
  return fillMissingFieldCodes(itemList).map(item => ({
    id: item.id,
    parent_value: item.parentValue,
    field_code: item.field_code ?? '', // #3175。fillMissingFieldCodes 済みのため通常は空にならない
    type: item.type ?? DEFAULT_DATA_TYPE, // デフォルト: string
    display_width: item.displayWidth ?? 1, // デフォルト: 1（通常幅）
    is_active: item.is_active ?? true, // デフォルト: true（有効）
    is_ocr_target: item.is_ocr_target ?? true, // デフォルト: true（OCR対象。#2968）
    extraction_rule: item.extractionRule ?? '', // デフォルト: 空文字
    // 日付型の出力形式。デフォルトは空文字（未指定）。
    // #3144: 選択肢外の不正な値が保存されると dayjs が解釈できず壊れた文字列が残るため、
    // 全保存経路が通るここで最終的に検証する。
    output_format: toSafeOutputFormat(item.outputFormat),
    // 固定値（#3073）。値が取得できなかった項目に入れる値。デフォルトは空文字（未設定）
    fixed_value: item.fixedValue ?? '',
    // 計算フィールド設定（#3145）。計算できない型に変更されていればクリアする（#3195）
    ...toSafeCalculation(item.type, item.is_calculated, item.formula),
    // 選択肢（#3075）。ラジオボタン・プルダウン以外の型では空配列にクリアする
    choices: toSafeChoices(item.type, item.choices),
    ...toSafeValidation(
      item.type,
      toValidationSnake({
        ...item,
        // 要注意にする選択肢（#3357）。選択肢型以外・現在の選択肢に無い値は落とす
        reviewChoiceValues: toSafeReviewChoiceValues(
          item.type,
          toSafeChoices(item.type, item.choices),
          item.reviewChoiceValues,
        ),
      }),
    ),
    children:
      item.children?.map(child => ({
        id: child.id,
        child_value: child.childValue,
        field_code: child.field_code ?? '', // #3175
        type: child.type ?? DEFAULT_DATA_TYPE, // デフォルト: string
        display_width: child.displayWidth ?? 1, // デフォルト: 1（通常幅）
        is_active: child.is_active ?? true, // デフォルト: true（有効）
        is_ocr_target: child.is_ocr_target ?? true, // デフォルト: true（OCR対象。#2968）
        extraction_rule: child.extractionRule ?? '', // デフォルト: 空文字
        output_format: toSafeOutputFormat(child.outputFormat), // #3144: 上と同様に検証する
        fixed_value: child.fixedValue ?? '', // 固定値（#3073）
        // 計算フィールド設定（#3145）。計算できない型ならクリアする（#3195）
        ...toSafeCalculation(child.type, child.is_calculated, child.formula),
        choices: toSafeChoices(child.type, child.choices), // 選択肢（#3075）
        ...toSafeValidation(
          child.type,
          toValidationSnake({
            ...child,
            // 要注意にする選択肢（#3357）
            reviewChoiceValues: toSafeReviewChoiceValues(
              child.type,
              toSafeChoices(child.type, child.choices),
              child.reviewChoiceValues,
            ),
          }),
        ),
      })) || [],
  }));
};

/**
 * Firestore から取得した snake_case の itemList を camelCase に変換する。
 */
export const convertItemListToCamelCase = (itemList: ItemListRaw[]): ItemListType[] => {
  return itemList.map(item => ({
    id: item.id,
    parentValue: item.parent_value,
    // #3175: 導入前に作られた既存パターンは未設定。UI 側で項目名から補完して表示する
    field_code: item.field_code ?? '',
    type: item.type,
    displayWidth: item.display_width,
    is_active: item.is_active !== undefined ? item.is_active : true,
    is_ocr_target: item.is_ocr_target !== undefined ? item.is_ocr_target : true,
    extractionRule: item.extraction_rule ?? '',
    outputFormat: item.output_format ?? '',
    fixedValue: item.fixed_value ?? '',
    // 計算フィールド設定（#3145）。#3195 の対応前に保存された不正な組み合わせを読み込み時にも落とす
    ...toSafeCalculation(item.type, item.is_calculated, item.formula),
    // #3075: 導入前の既存パターンには存在しないフィールド。空配列で補う
    choices: toSafeChoices(item.type, normalizeChoiceList(item.choices)),
    ...toValidationCamel(
      toSafeValidation(item.type, {
        ...item,
        review_choice_values: toStringArray(item.review_choice_values),
      }),
    ),
    children: (item.children || []).map(
      (child): ItemListChildType => ({
        id: child.id,
        childValue: child.child_value,
        field_code: child.field_code ?? '', // #3175
        type: child.type,
        displayWidth: child.display_width,
        is_active: child.is_active !== undefined ? child.is_active : true,
        is_ocr_target: child.is_ocr_target !== undefined ? child.is_ocr_target : true,
        extractionRule: child.extraction_rule ?? '',
        outputFormat: child.output_format ?? '',
        fixedValue: child.fixed_value ?? '',
        // 計算フィールド設定（#3145）。上と同様に読み込み時にも落とす（#3195）
        ...toSafeCalculation(child.type, child.is_calculated, child.formula),
        // #3075: 導入前の既存パターンには存在しないフィールド。空配列で補う
        choices: toSafeChoices(child.type, normalizeChoiceList(child.choices)),
        ...toValidationCamel(
          toSafeValidation(child.type, {
            ...child,
            review_choice_values: toStringArray(child.review_choice_values),
          }),
        ),
      }),
    ),
  }));
};
